import {
  Button,
  Table,
  EmptyState,
  ErrorBanner,
  Badge,
} from "frontend-kit/components/index.js";
import { getChainApp, getBlockHeight } from "./chain-app.js";
import { luxToDusk } from "./chain-config.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  getParticipantActivity,
  listMarketsByCommitment,
  loadBackendConfig,
} from "./gateway-api.js";
import { isParticipantReady, loadParticipant } from "./participant-store.js";
import { loadTradingKey } from "./trading-key-store.js";
import {
  OP,
  encodeClaimPayload,
  signTraderAuth,
  buildTraderAuthArg,
  hexToBytes,
} from "./trader-crypto.js";
import { showGlobalTx, trackTx } from "./tx-status.js";
import { sellCollateralOut } from "./trade-ticket.js";
import { withNonceRetry } from "./trader-nonce.js";
import { isAutoRetryEnabled } from "./trade-settings.js";
import { explorerTxUrl, shortTxHash } from "./explorer.js";
import { icon } from "./icons.js";
import { onChainCategoryLabel } from "./category-colors.js";
import {
  effectiveStatus,
  isLiveStatus,
  isResolvedStatus,
} from "./market-status.js";

function priceYesFromReserves(market) {
  const yes = Number(market.yes_reserve ?? market.yesReserve);
  const no = Number(market.no_reserve ?? market.noReserve);
  if (yes > 0 || no > 0) {
    const sum = yes + no;
    if (sum > 0) return no / sum;
  }
  return null;
}

function isClaimable(row) {
  if (row.claimed) return false;
  if (row.status === "finalized" && row.winning != null) {
    const winningShares = row.winning === 0 ? row.yes : row.no;
    return winningShares > 0;
  }
  if (row.status === "cancelled" && (row.yes > 0 || row.no > 0)) return true;
  return false;
}

function filterRows(rows, filter) {
  if (filter === "open") return rows.filter((r) => isLiveStatus(r.status) && (r.yes > 0 || r.no > 0));
  if (filter === "claimable") return rows.filter(isClaimable);
  if (filter === "resolved") return rows.filter((r) => isResolvedStatus(r.status));
  return rows;
}

const ACTION_LABELS = {
  deposited: "Deposited",
  withdrawn: "Withdrew",
  payout_claimed: "Claimed",
  cancelled_redeemed: "Redeemed",
};

function actionLabel(row) {
  if (row.event_name === "traded") return row.is_buy === false ? "Sold" : "Bought";
  return ACTION_LABELS[row.event_name] ?? row.event_name ?? "-";
}

/** Direction of free-collateral movement - "in" grows it, "out" spends it. */
function activityDirection(row) {
  if (row.event_name === "traded") return row.is_buy === false ? "in" : "out";
  if (row.event_name === "deposited" || row.event_name === "payout_claimed" || row.event_name === "cancelled_redeemed") {
    return "in";
  }
  if (row.event_name === "withdrawn") return "out";
  return "";
}

/**
 * "traded" rows carry `amount`/`shares_or_out` with swapped meaning
 * depending on side: a buy spends `amount` lux to receive `shares_or_out`
 * shares, a sell spends `amount` shares to receive `shares_or_out` lux. Non-trade
 * rows (deposit/withdraw/claim/redeem) only ever have a lux `amount`.
 */
function tradeSplit(row) {
  if (row.event_name !== "traded") {
    return { shares: null, duskLux: row.amount != null ? BigInt(row.amount) : null };
  }
  const a = row.amount != null ? BigInt(row.amount) : null;
  const s = row.shares_or_out != null ? BigInt(row.shares_or_out) : null;
  return row.is_buy === false ? { shares: a, duskLux: s } : { shares: s, duskLux: a };
}

/**
 * @param {{ log?: Function, onOpenMarket?: Function, onRefresh?: Function, onOpenSettings?: Function, onCollateralChange?: Function }} opts
 */
export async function renderPortfolioPage({
  log,
  onOpenMarket,
  onRefresh,
  onOpenSettings,
  onCollateralChange,
}) {
  const root = document.createElement("div");
  root.className = "portfolio-page";

  const key = loadTradingKey();
  const participant = loadParticipant();
  if (!isParticipantReady(participant) || !key?.commitment_hex) {
    const empty = EmptyState({
      title: "Finish account setup",
      description:
        "Get a participant id, prove your Moonlight wallet, then link a trading key before depositing.",
    });
    root.append(empty);
    root.append(
      Button({
        label: "Open account settings",
        variant: "primary",
        onClick: () => onOpenSettings?.() || (location.hash = "#/settings"),
      })
    );
    return root;
  }

  const header = document.createElement("div");
  header.className = "portfolio-header";
  const title = document.createElement("h1");
  title.textContent = "Portfolio";
  const profileLink = document.createElement("a");
  profileLink.href = `#/trader/${key.commitment_hex}`;
  profileLink.className = "link-btn portfolio-profile-link";
  profileLink.textContent = "View profile";
  profileLink.title = "Open public trader profile";
  header.append(title, profileLink);
  root.append(header);

  const cfg = loadBackendConfig();
  let markets = [];
  try {
    markets = await listMarketsByCommitment(cfg, key.commitment_hex);
  } catch (e) {
    root.append(ErrorBanner({ message: e.message || String(e) }));
  }
  const marketTitleById = new Map(
    markets.map((m) => [Number(m.market_id), m.metadata?.title ?? `Event #${m.market_id}`])
  );

  let activityItems = [];
  try {
    const activity = await getParticipantActivity(cfg, participant.participant_id);
    activityItems = activity?.activity ?? [];
  } catch {
    /* activity optional for settlement stats */
  }

  /** @type {Map<number, number>} market_id → total claimed lux */
  const claimedByMarket = new Map();
  let claimsCount = 0;
  let claimedLuxTotal = 0n;
  let redeemCount = 0;
  let redeemedLuxTotal = 0n;
  for (const item of activityItems) {
    const name = String(item.event_name || "").toLowerCase();
    const amt = item.amount != null ? BigInt(item.amount) : 0n;
    if (name === "payout_claimed") {
      claimsCount += 1;
      claimedLuxTotal += amt;
      if (item.market_id != null) {
        const id = Number(item.market_id);
        claimedByMarket.set(id, (claimedByMarket.get(id) ?? 0) + Number(amt));
      }
    } else if (name === "cancelled_redeemed") {
      redeemCount += 1;
      redeemedLuxTotal += amt;
    }
  }

  const dusk = await getChainApp();
  const commitment = Array.from(hexToBytes(key.commitment_hex));
  const [collateralSettled, tipSettled, positionBundles] = await Promise.all([
    dusk
      .readContract({
        contract: "predictionMarket",
        functionName: "collateral_of",
        args: commitment,
      })
      .catch(() => 0n),
    getBlockHeight().catch(() => null),
    Promise.all(
      markets.map(async (m) => {
        let yes = 0;
        let no = 0;
        let p = priceYesFromReserves(m);
        let closeBlock = null;
        let onChainStatus = m.status;
        let yesReserve = null;
        let noReserve = null;
        try {
          const [pos, priceYes, info] = await Promise.all([
            dusk.readContract({
              contract: "predictionMarket",
              functionName: "position_of",
              args: [m.market_id, commitment],
            }),
            dusk
              .readContract({
                contract: "predictionMarket",
                functionName: "price_yes",
                args: m.market_id,
              })
              .catch(() => null),
            dusk
              .readContract({
                contract: "predictionMarket",
                functionName: "market_info",
                args: m.market_id,
              })
              .catch(() => null),
          ]);
          yes = Number(pos?.[0] ?? 0);
          no = Number(pos?.[1] ?? 0);
          if (priceYes != null) p = Number(priceYes) / 1e18;
          if (info) {
            closeBlock = info.close_block ?? null;
            if (info.status != null) onChainStatus = info.status;
            if (info.winning_outcome != null) m.winning_outcome = info.winning_outcome;
            yesReserve = info.yes_reserve ?? info[0] ?? null;
            noReserve = info.no_reserve ?? info[1] ?? null;
            if (p == null) {
              const yr = Number(yesReserve ?? 0);
              const nr = Number(noReserve ?? 0);
              if (yr + nr > 0) p = nr / (yr + nr);
            }
          } else if (p == null) {
            p = 0.5;
          }
        } catch {
          /* skip enrichment for this market */
        }
        if (p == null) p = 0.5;
        return { m, yes, no, p, closeBlock, onChainStatus, yesReserve, noReserve };
      })
    ),
  ]);

  let collateral = collateralSettled;
  let blockHeight = tipSettled;

  const rows = [];
  let totalMtm = 0;
  let claimable = 0;
  let openCount = 0;

  for (const { m, yes, no, p, closeBlock, onChainStatus, yesReserve, noReserve } of positionBundles) {
    const status = effectiveStatus(onChainStatus ?? m.status, {
      closeBlock,
      blockHeight,
    });
    const mtmLux = yes * p + no * (1 - p);
    totalMtm += mtmLux;
    // "Sell now" - real FPMM sell math (same fn trade-ticket.js uses),
    // not spot-price × shares. mtmLux above ignores slippage; this is
    // what you'd actually get quoted to sell right now. YES/NO legs
    // priced independently against the *same* current reserves (not
    // chained sell-then-sell) - exact if only one side is held, a close
    // estimate if both are (selling one leg first would move the price
    // the second leg sees). Null (pool/reserves unavailable, or size
    // would drain the pool) shown as "-", not a wrong number.
    let sellNowLux = null;
    if (yesReserve != null && noReserve != null) {
      try {
        const yr = BigInt(yesReserve);
        const nr = BigInt(noReserve);
        let total = 0n;
        if (yes > 0) {
          const out = sellCollateralOut(yr, nr, 0, BigInt(yes));
          if (out == null) throw new Error("drain");
          total += out;
        }
        if (no > 0) {
          const out = sellCollateralOut(yr, nr, 1, BigInt(no));
          if (out == null) throw new Error("drain");
          total += out;
        }
        sellNowLux = total;
      } catch {
        sellNowLux = null;
      }
    }
    const maxPayoutLux = BigInt(yes) + BigInt(no); // 1 lux/share (= 1e-9 DUSK) if this side wins
    if (isLiveStatus(status) && (yes > 0 || no > 0)) openCount += 1;
    const priorClaim = claimedByMarket.get(Number(m.market_id));
    const alreadyClaimed = priorClaim != null && yes === 0 && no === 0;
    if (status === "finalized" && m.winning_outcome != null && !alreadyClaimed) {
      const winningShares = m.winning_outcome === 0 ? yes : no;
      if (winningShares > 0) claimable += 1;
    }
    rows.push({
      market_id: m.market_id,
      title: m.metadata?.title ?? `Event #${m.market_id}`,
      category: onChainCategoryLabel(m.market_category) ?? "-",
      status,
      yes,
      no,
      mtmLux,
      mtm: luxToDusk(mtmLux).toFixed(4),
      sellNowLux,
      maxPayoutLux,
      yesPct: `${(Math.round(p * 1000) / 10).toFixed(1)}¢`,
      volume: m.volume != null ? luxToDusk(m.volume) : "-",
      winning: m.winning_outcome,
      claimed: alreadyClaimed,
      claimPayout: priorClaim ?? null,
    });
  }

  const totalValueLux = BigInt(collateral) + BigInt(Math.round(totalMtm));
  let unrealizedPnlLux = 0;
  for (const row of rows) {
    if (!isLiveStatus(row.status) || (row.yes <= 0 && row.no <= 0)) continue;
    const mark = Number(row.mtmLux) || 0;
    const sell = row.sellNowLux != null ? Number(row.sellNowLux) : mark;
    unrealizedPnlLux += sell - mark;
  }

  const hero = document.createElement("div");
  hero.className = "portfolio-stat-grid";

  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.className = "icon-btn portfolio-refresh-btn";
  refreshBtn.title = "Refresh portfolio";
  refreshBtn.setAttribute("aria-label", "Refresh portfolio");
  refreshBtn.append(icon("refresh", { size: 16 }));
  refreshBtn.addEventListener("click", async () => {
    refreshBtn.disabled = true;
    try {
      const next = await renderPortfolioPage({
        log,
        onOpenMarket,
        onRefresh,
        onOpenSettings,
        onCollateralChange,
      });
      root.replaceWith(next);
    } catch (e) {
      log?.(`portfolio refresh failed: ${e.message || e}`);
      refreshBtn.disabled = false;
    }
  });

  function statCard(label, valueNode, { hint = "", extra = null } = {}) {
    const card = document.createElement("div");
    card.className = "summary-card dt-card portfolio-stat-card";
    const head = document.createElement("div");
    head.className = "portfolio-stat-head";
    const l = document.createElement("div");
    l.className = "stat-label dt-th";
    l.textContent = label;
    head.append(l);
    if (extra) head.append(extra);
    const v = document.createElement("div");
    v.className = "stat-value dt-val portfolio-stat-value";
    v.append(valueNode);
    card.append(head, v);
    if (hint) {
      const h = document.createElement("div");
      h.className = "hint";
      h.append(hint);
      card.append(h);
    }
    return card;
  }

  const pnlNode = document.createElement("span");
  pnlNode.className =
    unrealizedPnlLux > 0
      ? "pnl-positive"
      : unrealizedPnlLux < 0
        ? "pnl-negative"
        : "";
  const pnlPrefix = unrealizedPnlLux > 0 ? "+" : unrealizedPnlLux < 0 ? "−" : "";
  pnlNode.append(
    pnlPrefix,
    duskAmountNode(BigInt(Math.abs(Math.round(unrealizedPnlLux))), { decimals: 4 })
  );

  const freeRefresh = document.createElement("div");
  freeRefresh.className = "portfolio-free-label-row";
  freeRefresh.append(refreshBtn);

  const totalCard = statCard("Total value", duskAmountNode(totalValueLux, { decimals: 4 }), {
    hint: "Free collateral + open positions (mark)",
  });
  const pnlCard = statCard("Unrealized P&L", pnlNode, {
    hint: "Sell-now vs mark on open positions",
  });
  const openCard = statCard("Open positions", String(openCount));
  const freeCard = statCard(
    "Free collateral",
    duskAmountNode(collateral),
    { hint: "Available to trade", extra: freeRefresh }
  );

  hero.append(totalCard, pnlCard, openCard, freeCard);
  root.append(hero);

  const claimBannerHost = document.createElement("div");
  claimBannerHost.className = "portfolio-claim-banner-host";
  root.append(claimBannerHost);

  function paintClaimBanner(count) {
    claimBannerHost.replaceChildren();
    if (!(count > 0)) return;
    const banner = document.createElement("button");
    banner.type = "button";
    banner.className = "portfolio-claim-banner";
    banner.textContent =
      count === 1
        ? "1 payout ready - view Claimable"
        : `${count} payouts ready - view Claimable`;
    banner.addEventListener("click", () => {
      activeFilter = "claimable";
      paintFilterCounts();
      renderPositionsTable();
      filterRow.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    claimBannerHost.append(banner);
  }
  paintClaimBanner(claimable);

  if (claimsCount > 0 || redeemCount > 0) {
    const record = document.createElement("div");
    record.className = "portfolio-settlement-record";
    record.append("Settlement record · ");
    let first = true;
    if (claimsCount > 0) {
      record.append(`${claimsCount} claim${claimsCount === 1 ? "" : "s"} · `, duskAmountNode(claimedLuxTotal));
      first = false;
    }
    if (redeemCount > 0) {
      record.append(
        `${first ? "" : " · "}${redeemCount} redeem${redeemCount === 1 ? "" : "s"} · `,
        duskAmountNode(redeemedLuxTotal)
      );
    }
    root.append(record);
  }

  const main = document.createElement("div");
  main.className = "portfolio-main portfolio-main--solo";

  const posCol = document.createElement("section");
  posCol.className = "portfolio-positions dt-card";
  const posTitle = document.createElement("h2");
  posTitle.className = "portfolio-section-title";
  posTitle.textContent = "Positions";
  posCol.append(posTitle);

  const resultHost = document.createElement("div");
  resultHost.className = "portfolio-result-host";
  posCol.append(resultHost);

  let activeFilter = "all";
  const filterRow = document.createElement("div");
  filterRow.className = "portfolio-filter-row";
  const tableHost = document.createElement("div");

  function showClaimResult({ kind, marketId, payout, title }) {
    const box = document.createElement("div");
    box.className = "portfolio-claim-result";
    box.append(
      Badge({
        variant: "success",
        label: kind === "claim" ? "Claimed" : "Redeemed",
      })
    );
    const p = document.createElement("p");
    const name = title || `#${marketId}`;
    if (kind === "claim") {
      p.append(`${name} resolved - `, duskAmountNode(payout), " moved to free collateral.");
    } else {
      p.append(`${name} cancelled - `, duskAmountNode(payout), " redeemed to free collateral.");
    }
    box.append(p);
    resultHost.replaceChildren(box);
  }

  function paintHeroCounts() {
    let nextOpen = 0;
    let nextClaimable = 0;
    let nextMtmLux = 0;
    let nextPnl = 0;
    for (const row of rows) {
      nextMtmLux += Number(row.mtmLux) || 0;
      if (isLiveStatus(row.status) && (row.yes > 0 || row.no > 0)) {
        nextOpen += 1;
        const mark = Number(row.mtmLux) || 0;
        const sell = row.sellNowLux != null ? Number(row.sellNowLux) : mark;
        nextPnl += sell - mark;
      }
      if (isClaimable(row)) nextClaimable += 1;
    }
    openCard.querySelector(".portfolio-stat-value").textContent = String(nextOpen);
    paintClaimBanner(nextClaimable);
    totalCard.querySelector(".portfolio-stat-value").replaceChildren(
      duskAmountNode(BigInt(collateral) + BigInt(Math.round(nextMtmLux)), { decimals: 4 })
    );
    const pnlWrap = pnlCard.querySelector(".portfolio-stat-value");
    pnlWrap.replaceChildren();
    const span = document.createElement("span");
    span.className =
      nextPnl > 0 ? "pnl-positive" : nextPnl < 0 ? "pnl-negative" : "";
    const prefix = nextPnl > 0 ? "+" : nextPnl < 0 ? "−" : "";
    span.append(prefix, duskAmountNode(BigInt(Math.abs(Math.round(nextPnl))), { decimals: 4 }));
    pnlWrap.append(span);
    freeCard.querySelector(".portfolio-stat-value").replaceChildren(duskAmountNode(collateral));
    paintFilterCounts();
  }

  function eventPill(marketId) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "event-pill";
    btn.textContent = `#${marketId}`;
    btn.title = `Open event #${marketId}`;
    btn.addEventListener("click", () => onOpenMarket?.(marketId));
    return btn;
  }

  function filterCounts() {
    return {
      all: rows.length,
      open: rows.filter((r) => isLiveStatus(r.status) && (r.yes > 0 || r.no > 0)).length,
      claimable: rows.filter(isClaimable).length,
      resolved: rows.filter((r) => isResolvedStatus(r.status)).length,
    };
  }

  function paintFilterCounts() {
    const counts = filterCounts();
    for (const btn of filterRow.querySelectorAll(".portfolio-filter-btn")) {
      const id = btn.dataset.filter;
      const base = btn.dataset.label;
      if (!id || !base) continue;
      const n = counts[id] ?? 0;
      btn.textContent = `${base} (${n})`;
      btn.classList.toggle("is-active", id === activeFilter);
    }
  }

  async function runPositionOp(kind, row) {
    const marketId = row.market_id;
    const payout =
      kind === "claim"
        ? row.winning === 0
          ? row.yes
          : row.no
        : row.yes + row.no;
    const ui = showGlobalTx({
      label: kind === "claim" ? `Claim event #${marketId}` : `Redeem event #${marketId}`,
    });
    try {
      const readChainNonce = async () =>
        Number(
          await dusk.readContract({
            contract: "predictionMarket",
            functionName: "next_nonce",
            args: commitment,
          })
        );
      const payload = encodeClaimPayload(marketId);

      async function attemptClaim(nonce) {
        const { signatureHex } = await signTraderAuth({
          skHex: key.sk_hex,
          op: kind === "claim" ? OP.CLAIM : OP.REDEEM,
          commitmentHex: key.commitment_hex,
          nonce,
          payloadBytes: payload,
        });
        const auth = buildTraderAuthArg({
          pkBase58: key.pk_base58,
          nonce,
          signatureHex,
        });
        const tx = await dusk.writeContract({
          contract: "predictionMarket",
          functionName: kind === "claim" ? "claim_payout" : "redeem_cancelled",
          args: { auth, market_id: marketId },
          privacy: "public",
        });
        return trackTx(tx, { ui, autoDismissMs: 8000 });
      }

      // Nonce reserved through the shared per-commitment queue so this
      // claim/redeem can't collide with a trade/deposit submitted from
      // elsewhere on the page moments apart - see trader-nonce.js. Retry
      // on a residual (cross-tab/device) race is opt-in via Settings.
      const tracked = await withNonceRetry(key.commitment_hex, readChainNonce, attemptClaim, {
        retryOnStaleNonce: isAutoRetryEnabled(),
        onRetrying: (e) => {
          ui.setStatus(
            "retrying",
            "That nonce was stale (another action beat it to the chain) - retrying with a fresh one…"
          );
          log?.(`${kind} rejected on a stale nonce (${e.message}) - retrying once with a fresh one`);
        },
      });
      log?.(
        `${kind} tx ${tracked.hash} executed (participant ${participant.participant_id})`
      );

      row.claimed = true;
      row.claimPayout = payout;
      row.yes = 0;
      row.no = 0;
      row.mtmLux = 0;
      row.mtm = "0.0000";
      collateral = BigInt(collateral) + BigInt(payout);
      if (kind === "claim") {
        claimsCount += 1;
        claimedLuxTotal += BigInt(payout);
        claimedByMarket.set(
          Number(marketId),
          (claimedByMarket.get(Number(marketId)) ?? 0) + Number(payout)
        );
      } else {
        redeemCount += 1;
        redeemedLuxTotal += BigInt(payout);
      }
      showClaimResult({ kind, marketId, payout, title: row.title });
      paintHeroCounts();
      onCollateralChange?.(collateral);
      renderPositionsTable();
    } catch (e) {
      ui.setStatus("failed", e.message || String(e));
      log?.(`${kind} failed: ${e.message || e}`);
    }
  }

  function renderPositionsTable() {
    const filtered = filterRows(rows, activeFilter);
    tableHost.replaceChildren();
    if (!rows.length) {
      tableHost.append(
        EmptyState({
          title: "No positions yet",
          description: "Deposit collateral from the Free chip in the header, then trade on an event.",
        }),
        Button({
          label: "Browse events",
          variant: "primary",
          onClick: () => {
            location.hash = "#/events";
          },
        })
      );
      return;
    }
    if (!filtered.length) {
      tableHost.append(
        Object.assign(document.createElement("p"), {
          className: "hint",
          textContent: "No positions in this filter.",
        })
      );
      return;
    }
    const scroll = document.createElement("div");
    scroll.className = "portfolio-table-scroll";
    scroll.append(
      Table({
        columns: [
          {
            header: "Market",
            render: (row) => {
              const wrap = document.createElement("div");
              wrap.className = "event-cell";
              wrap.append(eventPill(row.market_id));
              const titleBtn = document.createElement("button");
              titleBtn.type = "button";
              titleBtn.className = "link-btn";
              titleBtn.textContent = row.title;
              titleBtn.addEventListener("click", () => onOpenMarket?.(row.market_id));
              wrap.append(titleBtn);
              return wrap;
            },
          },
          {
            header: "Side",
            render: (row) => {
              const wrap = document.createElement("div");
              wrap.className = "side-chips";
              if (row.yes > 0) {
                const y = document.createElement("span");
                y.className = "side-chip is-yes";
                y.textContent = "YES";
                wrap.append(y);
              }
              if (row.no > 0) {
                const n = document.createElement("span");
                n.className = "side-chip is-no";
                n.textContent = "NO";
                wrap.append(n);
              }
              if (!wrap.childNodes.length) wrap.textContent = "-";
              return wrap;
            },
          },
          {
            header: "Shares",
            render: (row) => {
              // 1 share = 1 lux - show as DUSK amounts, not raw lux integers.
              const wrap = document.createElement("span");
              wrap.className = "portfolio-shares";
              const parts = [];
              if (row.yes > 0) {
                const y = document.createElement("span");
                y.append("Y ", duskAmountNode(row.yes, { decimals: 4 }));
                parts.push(y);
              }
              if (row.no > 0) {
                const n = document.createElement("span");
                n.append("N ", duskAmountNode(row.no, { decimals: 4 }));
                parts.push(n);
              }
              if (!parts.length) return "-";
              for (let i = 0; i < parts.length; i++) {
                if (i > 0) wrap.append(" · ");
                wrap.append(parts[i]);
              }
              return wrap;
            },
          },
          {
            header: "Avg",
            render: () => "-",
          },
          {
            header: "Mark",
            // mtmLux is lux (share-weighted); duskAmountNode defaults to lux→dusk.
            render: (row) => duskAmountNode(row.mtmLux, { decimals: 4 }),
          },
          {
            header: "P&L",
            render: (row) => {
              if (row.claimed && row.claimPayout != null) {
                const span = document.createElement("span");
                span.className = "pnl-positive";
                span.append("+", duskAmountNode(row.claimPayout, { decimals: 4 }));
                return span;
              }
              if (row.status === "finalized" && row.winning != null) {
                const winningShares = row.winning === 0 ? row.yes : row.no;
                const losing = row.yes + row.no - winningShares;
                if (winningShares <= 0 && losing > 0) {
                  const span = document.createElement("span");
                  span.className = "pnl-negative";
                  span.textContent = "Lost";
                  return span;
                }
              }
              if (!isLiveStatus(row.status) || (row.yes <= 0 && row.no <= 0)) {
                return "-";
              }
              const mark = Number(row.mtmLux) || 0;
              const sell = row.sellNowLux != null ? Number(row.sellNowLux) : mark;
              const delta = sell - mark;
              const span = document.createElement("span");
              span.className =
                delta > 0 ? "pnl-positive" : delta < 0 ? "pnl-negative" : "";
              const prefix = delta > 0 ? "+" : delta < 0 ? "−" : "";
              span.append(
                prefix,
                duskAmountNode(BigInt(Math.abs(Math.round(delta))), { decimals: 4 })
              );
              return span;
            },
          },
          {
            header: "",
            render: (row) => {
              const wrap = document.createElement("div");
              wrap.className = "row portfolio-row-actions";
              wrap.append(
                Button({
                  label: "Open",
                  onClick: () => onOpenMarket?.(row.market_id),
                })
              );
              if (row.claimed) return wrap;
              if (row.status === "finalized" && row.winning != null) {
                const winningShares = row.winning === 0 ? row.yes : row.no;
                if (winningShares > 0) {
                  wrap.append(
                    Button({
                      label: `Claim ${luxToDusk(winningShares)}`,
                      variant: "primary",
                      onClick: () => runPositionOp("claim", row),
                    })
                  );
                }
              }
              if (row.status === "cancelled" && (row.yes > 0 || row.no > 0)) {
                wrap.append(
                  Button({
                    label: `Redeem ${luxToDusk(row.yes + row.no)}`,
                    onClick: () => runPositionOp("redeem", row),
                  })
                );
              }
              return wrap;
            },
          },
        ],
        rows: filtered,
      })
    );
    tableHost.append(scroll);
  }

  const filters = [
    ["all", "All"],
    ["open", "Open"],
    ["claimable", "Claimable"],
    ["resolved", "Resolved"],
  ];
  for (const [id, label] of filters) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "portfolio-filter-btn";
    btn.dataset.filter = id;
    btn.dataset.label = label;
    btn.addEventListener("click", () => {
      activeFilter = id;
      paintFilterCounts();
      renderPositionsTable();
    });
    filterRow.append(btn);
  }
  paintFilterCounts();
  posCol.append(filterRow, tableHost);
  renderPositionsTable();

  main.append(posCol);
  root.append(main);

  // --- Activity ---
  const act = document.createElement("section");
  act.className = "portfolio-activity dt-card";
  const actTitle = document.createElement("h2");
  actTitle.className = "portfolio-section-title";
  actTitle.textContent = "Recent activity";
  act.append(actTitle);

  const items = [...activityItems]
    .sort((a, b) => Number(b.block_number ?? 0) - Number(a.block_number ?? 0))
    .slice(0, 20);
  if (!items.length) {
    act.append(
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent:
          "Deposits, trades, and claims appear here once the gateway indexes them. Showing latest 20.",
      })
    );
  } else {
    act.append(
      Object.assign(document.createElement("p"), {
        className: "hint",
        textContent: "Showing latest 20 events.",
      })
    );
    act.append(
      Table({
        columns: [
          {
            header: "Block",
            render: (row) => String(row.block_number ?? "-"),
          },
          {
            header: "Event",
            render: (row) => {
              if (row.market_id == null) return "-";
              const wrap = document.createElement("div");
              wrap.className = "event-cell";
              wrap.append(eventPill(row.market_id));
              const title = marketTitleById.get(Number(row.market_id));
              if (title) {
                const titleBtn = document.createElement("button");
                titleBtn.type = "button";
                titleBtn.className = "link-btn";
                titleBtn.textContent = title;
                titleBtn.addEventListener("click", () => onOpenMarket?.(row.market_id));
                wrap.append(titleBtn);
              }
              return wrap;
            },
          },
          {
            header: "Action",
            render: (row) => actionLabel(row),
          },
          {
            header: "Side",
            render: (row) => {
              if (row.event_name !== "traded" || row.outcome == null) return "-";
              const span = document.createElement("span");
              span.className = `side-chip ${row.outcome === 0 ? "is-yes" : "is-no"}`;
              span.textContent = row.outcome === 0 ? "YES" : "NO";
              return span;
            },
          },
          {
            header: "Shares",
            render: (row) => {
              const { shares } = tradeSplit(row);
              // 1 share = 1 lux
              return shares != null ? duskAmountNode(shares, { decimals: 4 }) : "-";
            },
          },
          {
            header: "Amount",
            render: (row) => {
              const { duskLux } = tradeSplit(row);
              const span = document.createElement("span");
              const dir = activityDirection(row);
              span.className =
                dir === "in" ? "amount-in" : dir === "out" ? "amount-out" : "";
              span.append(duskLux != null ? duskAmountNode(duskLux) : "-");
              return span;
            },
          },
          {
            header: "Tx",
            render: (row) => {
              const wrap = document.createElement("div");
              wrap.className = "tx-cell";
              const hash = row.transaction_hash;
              if (!hash) {
                wrap.textContent = "-";
                return wrap;
              }
              const url = explorerTxUrl(hash);
              if (url) {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                a.textContent = shortTxHash(hash);
                a.title = "Open in Dusk explorer";
                wrap.append(a);
                wrap.append(icon("external", { size: 14 }));
              } else {
                wrap.textContent = shortTxHash(hash);
              }
              const copyBtn = document.createElement("button");
              copyBtn.type = "button";
              copyBtn.className = "copy-icon-btn";
              copyBtn.title = "Copy hash";
              copyBtn.append(icon("copy", { size: 14 }));
              copyBtn.addEventListener("click", async () => {
                try {
                  await navigator.clipboard.writeText(String(hash).replace(/^0x/i, ""));
                } catch {
                  /* ignore */
                }
              });
              wrap.append(copyBtn);
              return wrap;
            },
          },
        ],
        rows: items,
      })
    );
  }
  root.append(act);

  return root;
}
